import * as React from 'react';
import { AnyAction, Dispatch } from 'redux';
import { takeEvery, takeLeading } from 'redux-saga/effects';
import { CreateClientViewModel } from 'stageclip-common';

import { AddClientsRequest } from '../api/definitions/addClientRequest';
import { EditClientRequest } from '../api/definitions/editClientRequest';
import { GetAllClientsRequest } from '../api/definitions/getAllClientsRequest';
import { GetDefaultClipTemplateConfiguration } from '../api/definitions/getDefaultClipTemplateConfiguration';
import * as ClientNormalizers from '../api/normalizers/clientNormalizers';
import * as EntityNormalizers from '../api/normalizers/entityNormalizers';
import { DataRequestActionTypes, InternalActionTypes } from '../constants/actionTypes';
import { FormId } from '../constants/formId';
import { formatClipConfiguration } from '../helpers/clipConfigurationHelper';

import { GetSummaryReportRequest } from 'api/definitions/getClientSummaryRequest';
import { RequestAction } from './allActions';
import { createUpdateFormDataAction, FormSubmitAction, UpdateFormDataAction } from './formActions';
import { createModalCloseAction } from './modalActions';
import { runApiRequest } from './saga/sagaActions';

export interface ActiveClientsLoadedAction extends AnyAction {
  type: InternalActionTypes.ActiveClientsLoaded;
  result: EntityNormalizers.NormalizedEntityArray<ClientNormalizers.NormalizedClients>;
}

export interface ArchivedClientsLoadedAction extends AnyAction {
  type: InternalActionTypes.ArchivedClientsLoaded;
  result: EntityNormalizers.NormalizedEntityArray<ClientNormalizers.NormalizedClients>;
}

export interface ClientCreatedAction extends AnyAction {
  type: InternalActionTypes.ClientCreated;
  result: EntityNormalizers.NormalizedSingleEntity<ClientNormalizers.NormalizedClients>;
}

export interface ClientEditedAction extends AnyAction {
  type: InternalActionTypes.ClientEdited;
  result: EntityNormalizers.NormalizedSingleEntity<ClientNormalizers.NormalizedClients>;
}

export interface DefaultClipConfigurationLoadedAction extends AnyAction {
  type: InternalActionTypes.DefaultClipConfigurationLoaded;
  result: EntityNormalizers.NormalizedSingleEntity<string>;
}

export interface AddClientAction extends FormSubmitAction<CreateClientViewModel> {
  type: DataRequestActionTypes.NewClientRequested;
}

export interface RequestActiveClientsAction extends RequestAction {
  type: DataRequestActionTypes.LoadingActiveClientsRequested;
}

export interface RequestArchivedClientsAction extends RequestAction {
  type: DataRequestActionTypes.LoadingArchivedClientsRequested;
}

export interface RequestDefaultClipConfigurationAction extends RequestAction {
  type: DataRequestActionTypes.DefaultClipConfigurationRequested;
}

export interface MarkClientUpdateAsReadAction extends RequestAction {
  type: DataRequestActionTypes.MarkClientUpdateAsReadAction;
  clientId: string;
}

export interface EditClientAction extends FormSubmitAction<CreateClientViewModel> {
  clientId: string;
  type: DataRequestActionTypes.EditClientRequested;
}

export interface GetClipTemplateMappingAction extends RequestAction {
  clientId: string;
  type: DataRequestActionTypes.GetMappedClipTemplateRequested;
}

export interface GetClientSummaryAction extends RequestAction {
  name: string;
  clientId: string;
  dateFrom: string;
  dateTo: string;
  type: DataRequestActionTypes.ClientSummaryClientRequested;
}

export interface SummaryDownloadedAction extends AnyAction {
  type: InternalActionTypes.SummaryDownloaded;
  result: EntityNormalizers.NormalizedSingleEntity<ClientNormalizers.NormalizedClients>;
}

export interface CopyClientClipConfigurationRequestedAction
  extends FormSubmitAction<{
    formId: string;
    clientClipConfiguration: string;
  }> {
  type: DataRequestActionTypes.CopyClipConfigurationFromClientRequested;
}

export interface ClientArchivedAction extends AnyAction {
  type: InternalActionTypes.ClientArchived;
  result: EntityNormalizers.NormalizedSingleEntity<ClientNormalizers.NormalizedClients>;
}

export interface ClientUnarchivedAction extends AnyAction {
  type: InternalActionTypes.ClientUnarchived;
  result: EntityNormalizers.NormalizedSingleEntity<ClientNormalizers.NormalizedClients>;
}

function createActiveClientsLoadedAction(
  result: EntityNormalizers.NormalizedEntityArray<ClientNormalizers.NormalizedClients>,
): ActiveClientsLoadedAction {
  return {
    result,
    type: InternalActionTypes.ActiveClientsLoaded,
  };
}

function createArchivedClientsLoadedAction(
  result: EntityNormalizers.NormalizedEntityArray<ClientNormalizers.NormalizedClients>,
): ArchivedClientsLoadedAction {
  return {
    result,
    type: InternalActionTypes.ArchivedClientsLoaded,
  };
}

function createClientCreatedAction(
  result: EntityNormalizers.NormalizedSingleEntity<ClientNormalizers.NormalizedClients>,
): ClientCreatedAction {
  return {
    result,
    type: InternalActionTypes.ClientCreated,
  };
}

function createClientEditedAction(
  result: EntityNormalizers.NormalizedSingleEntity<ClientNormalizers.NormalizedClients>,
): ClientEditedAction {
  return {
    result,
    type: InternalActionTypes.ClientEdited,
  };
}

function createDefaultClipConfigurationLoadedAction(
  result: EntityNormalizers.NormalizedSingleEntity<string>,
): DefaultClipConfigurationLoadedAction {
  return {
    result,
    type: InternalActionTypes.DefaultClipConfigurationLoaded,
  };
}

export function createRequestActiveClientsAction(
  component: React.Component,
  dispatch: Dispatch,
): RequestActiveClientsAction {
  return {
    dispatch,
    requestingComponent: component,
    type: DataRequestActionTypes.LoadingActiveClientsRequested,
  };
}

export function createRequestArchivedClientsAction(
  component: React.Component,
  dispatch: Dispatch,
): RequestArchivedClientsAction {
  return {
    dispatch,
    requestingComponent: component,
    type: DataRequestActionTypes.LoadingArchivedClientsRequested,
  };
}

export function createRequestDefaultClipConfigurationAction(
  component: React.Component,
  dispatch: Dispatch,
): RequestDefaultClipConfigurationAction {
  return {
    dispatch,
    requestingComponent: component,
    type: DataRequestActionTypes.DefaultClipConfigurationRequested,
  };
}

export function createCopyClipTemplateConfigurationAction(
  formId: string,
  clipConfiguration: string,
): UpdateFormDataAction {
  return createUpdateFormDataAction(formId, { clipConfiguration });
}

export function createCopyClipConfigurationFromClientRequestedAction(
  action: CopyClientClipConfigurationRequestedAction,
): void {
  action.dispatch(
    createCopyClipTemplateConfigurationAction(
      action.formData.formId,
      JSON.parse(action.formData.clientClipConfiguration),
    ),
  );
  action.dispatch(createModalCloseAction(FormId.CopyClipConfigurationFromClient));
}

export function markClientUpdateAsReadAction(
  clientId: string,
  component: React.Component,
  dispatch: Dispatch,
): MarkClientUpdateAsReadAction {
  return {
    clientId,
    dispatch,
    requestingComponent: component,
    type: DataRequestActionTypes.MarkClientUpdateAsReadAction,
  };
}

function* loadActiveClients(action: RequestActiveClientsAction) {
  const request = new GetAllClientsRequest(false, action.requestingComponent, action.dispatch);
  // @ts-ignore
  yield runApiRequest(request, createActiveClientsLoadedAction);
}

function* loadArchivedClients(action: RequestArchivedClientsAction) {
  const request = new GetAllClientsRequest(true, action.requestingComponent, action.dispatch);
  // @ts-ignore
  yield runApiRequest(request, createArchivedClientsLoadedAction);
}

function* addClient(action: AddClientAction) {
  formatClipConfiguration(action);
  const request = new AddClientsRequest(action.formData, action.requestingComponent, action.dispatch);

  // @ts-ignore
  yield runApiRequest(request, createClientCreatedAction);
}

function* getSummaryReport(action: GetClientSummaryAction) {
  yield new GetSummaryReportRequest(action.name, action.clientId, action.formData).downloadFile();
  action.dispatch(createModalCloseAction(FormId.DownloadSummaryForm));
}

function* editClient(action: EditClientAction) {
  formatClipConfiguration(action);
  const request = new EditClientRequest(action.clientId, action.formData, action.requestingComponent, action.dispatch);

  // @ts-ignore
  yield runApiRequest(request, createClientEditedAction);
}

function* loadDefaultClipConfiguration(action: RequestDefaultClipConfigurationAction) {
  const request = new GetDefaultClipTemplateConfiguration(action.requestingComponent, action.dispatch);

  // @ts-ignore
  yield runApiRequest(request, createDefaultClipConfigurationLoadedAction);
}

export function* createNewClientSaga() {
  yield takeEvery(DataRequestActionTypes.NewClientRequested, addClient);
}

export function* loadActiveClientsSaga() {
  yield takeLeading(DataRequestActionTypes.LoadingActiveClientsRequested, loadActiveClients);
}

export function* loadArchivedClientsSaga() {
  yield takeLeading(DataRequestActionTypes.LoadingArchivedClientsRequested, loadArchivedClients);
}

export function* copyClipConfigurationFromClientSaga() {
  yield takeLeading(
    DataRequestActionTypes.CopyClipConfigurationFromClientRequested,
    createCopyClipConfigurationFromClientRequestedAction,
  );
}
export function* editClientSaga() {
  yield takeEvery(DataRequestActionTypes.EditClientRequested, editClient);
}

export function* loadDownloadSummarySaga() {
  yield takeLeading(DataRequestActionTypes.ClientSummaryClientRequested, getSummaryReport);
}

export function* loadDefaultClipConfigurationSaga() {
  yield takeLeading(DataRequestActionTypes.DefaultClipConfigurationRequested, loadDefaultClipConfiguration);
}
// export function* markClientUpdateAsReadSaga(action: MarkClientUpdateAsReadAction) {
//   const request = new MarkClientUpdateAsReadRequest(action.clientId, action.requestingComponent, action.dispatch);

//   yield runApiRequest(request, markClientUpdateAsReadAction);
// }
